(ns frontend.view.settings2.sso.view
  (:require
   [ui.components.button.view :as button]
   [ui.components.modal.view :as modal]
   [frontend.db :as app-db]
   [frontend.view.settings2.sso.sub :as sub]
   [frontend.view.settings2.sso.event :as event]
   [frontend.state.current-session :as session]
   [ui.editors.labelled-input.view :as labelled-input]
   [common.schema.entities.login-page-config :as login-page-config-schema]
   [common.localization :as loc]
   [re-frame.core :as rf]
   [frontend.view.components.clipboard :as clipboard]
   [reagent.core :as r]
   [ui.util :as uu]
   [common.util :as cu]
   [ui.components.loading-spinner.view :as loading-spinner]
   [frontend.global-events.toast :as toast]
   [common.schema.constraints :as constraints]
   [frontend.actions.tenant :as tenant-actions]
   [ui.components.grid.view :as ag-grid]
   [oops.core :as oops]))

(defn delete-confirmation-modal
  [config on-confirm on-cancel]
  [modal/view
   {:visible? true
    :hide! on-cancel}
   [:span (loc/tr :delete-login-page)]
   [:div
    {:class "flex flex-col gap-3"}
    [:p
     (loc/tr :login-page-delete-confirmation-with-name [(:login-page-config/name config)])]
    [:p
     {:class "text-danger-light-text"}
     (loc/tr :this-action-cannot-be-undone)]]
   [:<>
    [button/sm-secondary-hollow
     {:on-click on-cancel}
     (loc/tr :cancel)]
    [button/sm-danger-solid
     {:on-click on-confirm}
     (loc/tr :delete)]]])

(defn create-button
  []
  (let [limit @(rf/subscribe [::sub/tenant-max-number-of-login-pages])
        can-add? @(rf/subscribe [::sub/can-add-login-configs?])]
    [button/sm-secondary-hollow
     {:on-click #(rf/dispatch [::event/open-creator])
      :disabled? (not can-add?)
      :tooltip (when-not can-add?
                 (loc/tr :login-page-configs-limit-reached-contact-support-cta [limit]))}
     [:i {:class "fa fa-plus"}]
     [:span]
     (loc/tr :create-new-login-page)]))

(defn login-page-configs-table
  [_configs]
  (let [copied-urls* (r/atom #{})]
    (fn [configs]
      (let [data configs
            copied-urls @copied-urls*]
        [:<>
         [:div
          {:class "flex flex-col gap-2"}
          [:div
           {:class "flex justify-between items-center"}
           [:p
            {:class "text-md font-semibold"}
            (loc/tr :login-pages)]
           [create-button]]

          [ag-grid/ag-grid-enterprise
           {:theme                         ag-grid/theme-quartz
            :rowData                       data
            :columnDefs [{:colId "configuration"
                          :headerName    (loc/tr :configuration)
                          :autoHeight   true
                          :cellRenderer (fn [js-args]
                                          (let [js-data (oops/oget js-args "data")
                                                clj-data (js->clj js-data :keywordize-keys true)
                                                config-name (:name clj-data)
                                                default-login-page? (:default-login-page? clj-data)]
                                            (r/as-element
                                             [:div
                                              {:class "flex flex-col gap-1 leading-tight py-3 overflow-hidden"}
                                              [:div
                                               {:class "font-normal truncate overflow-hidden"}
                                               config-name]
                                              (when default-login-page?
                                                [:div
                                                 {:class "flex justify-start"}
                                                 [:div
                                                  {:class "inline-block py-1 px-2 bg-complement-light text-complement-light-text text-xs rounded"}
                                                  (loc/tr :default)]])])))}
                         {:colId "sso-login-methods"
                          :headerName (loc/tr :login-page-config/sso-login-methods)
                          :cellRenderer (fn [js-args]
                                          (let [js-data (oops/oget js-args "data")
                                                clj-data (js->clj js-data :keywordize-keys true)
                                                sso-login-methods (:sso-login-methods clj-data)]
                                            (r/as-element
                                             [:div {:class "flex gap-2 font-normal items-center leading-tight h-full"}
                                              (when (some #{"azure"} sso-login-methods)
                                                [:span
                                                 {:class "px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded"}
                                                 (loc/tr :azure-sso)])

                                              (when (some #{"saml"} sso-login-methods)
                                                [:span
                                                 {:class "px-2 py-1 text-xs font-medium bg-cyan-100 text-cyan-800 rounded"}
                                                 (loc/tr :saml-sso)])])))}
                         {:colId "login-url"
                          :flex 1
                          :headerName (loc/tr :login-url)
                          :cellRenderer (fn [js-args]
                                          (let [js-data (oops/oget js-args "data")
                                                clj-data (js->clj js-data :keywordize-keys true)
                                                url (:full-url clj-data)
                                                copied? (contains? copied-urls url)]
                                            (r/as-element
                                             [:div
                                              {:class "flex items-center gap-2 overflow-hidden h-full"}
                                              [:div
                                               {:class "w-7  flex justify-center"}
                                               (if copied?
                                                 [:i.fal.fa-check.text-green-500]
                                                 [button/square-secondary-hollow
                                                  {:tooltip (loc/tr :copy-to-clipboard)
                                                   :on-click (fn [_]
                                                               (clipboard/copy-to url)
                                                               (swap! copied-urls* conj url)
                                                               (rf/dispatch [::toast/success (loc/tr :copied-to-clipboard)])
                                                               (uu/timeout 1500 #(swap! copied-urls* disj url)))
                                                   :class "p-1"}
                                                  [:i.fal.fa-copy]])]
                                              [:p
                                               {:class "text-sm break-all m-0 overflow-hidden font-thin text-ellipsis"}
                                               url]])))}
                         {:colId "actions"
                          :headerName ""
                          :pinned :right
                          :resizable false
                          :sortable false
                          :width 90
                          :cellRenderer (fn [js-args]
                                          (let [js-data (oops/oget js-args "data")
                                                id (oops/oget js-data "id")]
                                            (r/as-element
                                             [:div
                                              {:class "flex flex-col justify-center items-center h-full w-full"
                                               :on-mouse-enter (fn [e]
                                                                 (oops/ocall e "preventDefault")
                                                                 (oops/ocall e "stopPropagation"))}
                                              [:div {:class "flex gap-2"}
                                               [button/square-secondary-hollow
                                                {:on-click #(rf/dispatch [::event/edit-config id])
                                                 :tooltip (loc/tr :edit-login-page)}
                                                [:i {:class "fal fa-edit"}]]
                                               [button/square-danger-hollow
                                                {:on-click #(rf/dispatch [::event/show-delete-modal id])
                                                 :tooltip (loc/tr :delete-login-page)}
                                                [:i {:class "fal fa-trash"}]]]])))
                          :cellStyle {:padding "0px"}
                          :cellClass (uu/cm "border-r-transparent focus:border-y-transparent focus:border-l-[var(--ag-cell-horizontal-border)] focus:outline-none"
                                            "focus-within:border-y-transparent focus-within:border-l-[var(--ag-cell-horizontal-border)] focus-within:outline-none")}]
            :noRowsOverlayComponent        (fn [_js-args]
                                             (r/as-element
                                              [:div
                                               {:class "text-sm m-0 text-base-light-text-paler"}
                                               (loc/tr :no-login-pages-found)]))
            :domLayout                     :autoHeight
            :suppressContextMenu           true
            :suppressRowTransform          true
            :suppressRowHoverHighlight     true
            :pinnedTopRowData              []
            :suppressLastEmptyLineOnPaste  true
            :suppressMenuHide              true
            :stopEditingWhenCellsLoseFocus true
            :getRowId                      (fn [data-js]
                                             (str (oops/oget data-js "data.id")))
            :defaultColDef                 {:sortable                 false
                                            :filter                   false
                                            :cellClass                "absolute"
                                            :resizable                true
                                            :singleClickEdit          false
                                            :editable                 false
                                            :suppressHeaderMenuButton true
                                            :suppressMovable          false}}]]]))))

(defn block-password-login-field
  []
  (let [tenant-id (:db/id @session/tenant)
        tenant-cursor (r/cursor session/entities [tenant-id])
        attribute :tenant/block-password-login
        old-value (get @tenant-cursor attribute)
        options [{:value :yes
                  :label (loc/tr :yes)}
                 {:value :no
                  :label (loc/tr :no)}]]
    [labelled-input/view
     {:label     (loc/tr :company-settings-block-password-login)
      :data-type :data.type/keyword
      :value     (->> options
                      (cu/filter-by :value (if (:tenant/block-password-login @tenant-cursor)
                                             :yes
                                             :no))
                      (first))
      :options   options
      :on-change (fn [new-value]
                   (let [new-value (case new-value
                                     :yes true
                                     :no false)]
                     (when-not (= new-value old-value)
                       (swap! tenant-cursor assoc attribute new-value)
                       (deref tenant-cursor)
                       (tenant-actions/*->update! tenant-id
                                                  attribute
                                                  new-value
                                                  (fn [_]
                                                    (js/console.log "Update successful"))
                                                  (fn [err]
                                                    (when (= new-value old-value)
                                                      (swap! tenant-cursor assoc attribute old-value)
                                                      (deref tenant-cursor))
                                                    (js/console.log "Update failed " err))))))}]))

(defn list-main-content
  []
  (let [configs @(rf/subscribe [::sub/configs])
        delete-modal @(rf/subscribe [::sub/delete-modal])
        initialized? @(rf/subscribe [::sub/initialized?])
        loading-error? @(rf/subscribe [::sub/loading-error?])]
    [:div
     {:class "flex flex-col gap-6"}
     [:h2
      {:class "font-semibold text-xl"}
      (loc/tr :sso-configuration)]
     [block-password-login-field]

     (cond
       (not initialized?)
       [:div
        {:class "flex flex-col justify-center items-center h-48"}
        [loading-spinner/circle]]

       loading-error?
       [:div
        {:class "flex flex-col h-48 justify-center items-center"}
        [:p (loc/tr :an-error-occurred)]]

       :else
       [login-page-configs-table configs])

     (when delete-modal
       [delete-confirmation-modal
        (:config delete-modal)
        #(rf/dispatch [::event/delete-config (:config delete-modal)])
        #(rf/dispatch [::event/hide-delete-modal])])]))

(defn basic-info-card
  [{:keys [config on-change]}]
  (let [path-conflict? @(rf/subscribe [::sub/login-path-conflict?])
        touched-fields @(rf/subscribe [::sub/touched-fields])
        show-all-errors? @(rf/subscribe [::sub/show-all-errors?])
        path (:login-page-config/path config)
        path-validation-error (constraints/validate-sso-login-path-string-pattern path loc/tr)]
    [:div
     {:class "flex flex-col gap-4 p-4 border border-base-light-border-paler rounded-lg"}
     [:h5
      {:class "font-semibold text-md"}
      (loc/tr :basic-information)]
     [:div
      {:class "grid grid-cols-1 md:grid-cols-2 gap-4"}
      [:div
       {:class "flex flex-col gap-1"}
       [labelled-input/view
        {:data-type :data.type/text-simple
         :value (:login-page-config/name config)
         :label (loc/tr :name)
         :placeholder (loc/tr :login-page-name-placeholder-compliance-team)
         :on-change #(on-change :login-page-config/name %)
         :on-blur #(rf/dispatch [::event/set-field-touched :login-page-config/name])}]

       (when (and (or (contains? touched-fields :login-page-config/name)
                      show-all-errors?)
                  (cu/blank-value? (:login-page-config/name config)))
         [:p
          {:class "text-sm m-0 text-danger-regular"}
          (loc/tr :required)])]
      [:div
       {:class "flex flex-col gap-1"}
       [labelled-input/view
        {:data-type :data.type/text-simple
         :value path
         :label (loc/tr :login-path)
         :on-change #(on-change :login-page-config/path %)
         :on-blur #(rf/dispatch [::event/set-field-touched :login-page-config/path])}]

       (cond
         (and (not (contains? touched-fields :login-page-config/path))
              (not show-all-errors?)
              (cu/blank-value? (:login-page-config/path config)))
         nil

         (and (or (contains? touched-fields :login-page-config/path)
                  show-all-errors?)
              (cu/blank-value? (:login-page-config/path config)))
         [:p
          {:class "text-sm m-0 text-danger-regular"}
          (loc/tr :required)]

         path-validation-error
         [:p
          {:class "text-sm m-0 text-danger-regular"}
          path-validation-error]

         path-conflict?
         [:p
          {:class "text-sm m-0 text-danger-regular"}
          (loc/tr :this-path-is-already-reserved-please-select-another-one)])]

      [:div
       [labelled-input/view
        {:data-type :data.type/boolean
         :label (loc/tr :is-default-login-page?)
         :value (boolean (:default-config? config))
         :on-change #(on-change :default-config? %)}]]]]))

(defn saml-configuration-section
  [{:keys [config on-change]}]
  (let [metadata-url (str @app-db/server-url "/login/sso/saml/sp")]
    [:div {:class "flex flex-col gap-4"}
     [:div
      {:class "flex flex-col gap-2"}
      [:h6 {:class "font-semibold text-base mt-4"} "SAML Settings"]
      [:div
       {:class "flex flex-col gap-2 text-sm m-0"}
       [:p
        (loc/tr :saml-config-info)
        [:a
         {:target "zapflow-saml-sp"
          :href   metadata-url}
         metadata-url]]
       [:p (loc/tr :saml-config-info-2)]]]

     [:div {:class "grid grid-cols-1 md:grid-cols-2 gap-4"}
      [:div
       [labelled-input/view
        {:data-type :data.type/text-simple
         :value (:login-page-config/saml-idp-post-url config)
         :label (loc/tr :login-page-config/saml-idp-post-url)
         :on-change #(on-change :login-page-config/saml-idp-post-url %)}]]
      [:div
       [labelled-input/view
        {:data-type :data.type/text-simple
         :value (:login-page-config/saml-idp-redirect-url config)
         :label (loc/tr :login-page-config/saml-idp-redirect-url)
         :on-change #(on-change :login-page-config/saml-idp-redirect-url %)}]]]
     [:div {:class "grid grid-cols-1 md:grid-cols-2 gap-4"}
      [:div
       [labelled-input/view
        {:data-type :data.type/text-simple
         :value (:login-page-config/saml-idp-logout-url config)
         :label (loc/tr :login-page-config/saml-idp-logout-url)
         :on-change #(on-change :login-page-config/saml-idp-logout-url %)}]]
      [:div
       [labelled-input/view
        {:data-type :data.type/text-simple
         :value (:login-page-config/saml-idp-entity-id config)
         :label (loc/tr :login-page-config/saml-idp-entity-id)
         :on-change #(on-change :login-page-config/saml-idp-entity-id %)}]]]
     [:div
      [labelled-input/view
       {:data-type :data.type/text-multiline
        :value (:login-page-config/saml-idp-public-key config)
        :label (loc/tr :login-page-config/saml-idp-public-key)
        :on-change #(on-change :login-page-config/saml-idp-public-key %)}]]]))

(defn copyable-url-field
  [{:keys [label value]}]
  [:div
   {:class "flex flex-col gap-1.5"}
   [:label
    {:class "text-[14px] text-input-light-text font-semibold"}
    label]
   [:div
    {:class "flex"}
    [:div
     {:class "flex items-center gap-3 bg-complement-light rounded py-1 pr-1 pl-3"}
     [:div value]
     [button/sm-secondary-hollow
      {:class "bg-base-light hover:bg-base-light hover:opacity-75"
       :on-click (fn []
                   (clipboard/copy-to value)
                   (rf/dispatch [::toast/success (loc/tr :copied-to-clipboard)]))}
      [:i {:class "fal fa-copy"}]
      [:span (loc/tr :copy)]]]]])

(defn azure-configuration-section
  [{:keys [config on-change]}]
  (let [redirect-url (str @app-db/server-url "/login/sso/azure/auth-response")
        logout-url (str @app-db/server-url "/login/sso/azure/logout-response")]
    [:div {:class "flex flex-col gap-4"}
     [:h6
      {:class "font-semibold text-base mt-4"}
      (loc/tr :azure-sso-settings)]

     [:div
      [labelled-input/view
       {:data-type :data.type/keyword
        :editor-type :editor.type/radio
        :value (if (:login-page-config/use-custom-azure-sso-application? config)
                 :custom
                 :default)
        :label (loc/tr :azure-application-type)
        :on-change (fn [value]
                     (on-change :login-page-config/use-custom-azure-sso-application? (= value :custom)))
        :options [{:key :default
                   :value :default
                   :label "Use the Zapflow default application"}
                  {:key :custom
                   :value :custom
                   :label "Use your own Azure SSO application"}]}]]

     (when (:login-page-config/use-custom-azure-sso-application? config)
       [:<>
        [:div
         {:class "flex flex-col gap-4"}
         [:p
          {:class "text-base-light-text text-sm m-0"}
          (loc/tr :azure-config-info-2)]

         [copyable-url-field
          {:label (loc/tr :azure-redirect-url)
           :value redirect-url}]

         [copyable-url-field
          {:label (loc/tr :azure-logout-url)
           :value logout-url}]]


        [:div {:class "grid grid-cols-1 md:grid-cols-2 gap-4"}
         [:div
          [labelled-input/view
           {:data-type :data.type/text-simple
            :value (:login-page-config/azure-sso-tenant-id config)
            :label (loc/tr :login-page-config/azure-sso-tenant-id)
            :on-change #(on-change :login-page-config/azure-sso-tenant-id %)}]
          [:span {:class "text-xs"} "Directory (tenant) ID"]]

         [:div
          [labelled-input/view
           {:data-type :data.type/text-simple
            :value (:login-page-config/azure-sso-client-id config)
            :label (loc/tr :login-page-config/azure-sso-client-id)
            :on-change #(on-change :login-page-config/azure-sso-client-id %)}]
          [:span {:class "text-xs"} "Application (client) ID"]]]

        [:div {:class "grid grid-cols-1 md:grid-cols-2 gap-4"}
         [:div
          [labelled-input/view
           {:data-type :data.type/text-simple
            :value (:login-page-config/azure-sso-client-secret config)
            :label (loc/tr :login-page-config/azure-sso-client-secret)
            :field-type "password"
            :on-change #(on-change :login-page-config/azure-sso-client-secret %)}]
          [:span {:class "text-xs"} (loc/tr :azure-secret-value-placeholder)]]

         [:div
          [labelled-input/view
           {:data-type :data.type/text-simple
            :value (:login-page-config/azure-sso-authority config)
            :label (loc/tr :login-page-config/azure-sso-authority)
            :on-change #(on-change :login-page-config/azure-sso-authority %)}]
          [:span {:class "text-xs"} "https://login.microsoft.com/<Directory id>"]]]

        [:div
         [labelled-input/view
          {:data-type :data.type/text-simple
           :value (:login-page-config/azure-sso-scope config)
           :label (loc/tr :login-page-config/azure-sso-scope)
           :placeholder "https://graph.microsoft.com/.default"
           :on-change #(on-change :login-page-config/azure-sso-scope %)}]
         [:span {:class "text-xs"} "email"]]])]))

(defn sso-configuration-card
  [{:keys [config on-change]}]
  (let [has-azure? @(rf/subscribe [::sub/has-azure-sso?])
        has-saml? @(rf/subscribe [::sub/has-saml-sso?])
        touched-fields @(rf/subscribe [::sub/touched-fields])
        show-all-errors? @(rf/subscribe [::sub/show-all-errors?])]
    [:div
     {:class "flex flex-col gap-4 p-4 border border-base-light-border-paler rounded-lg"}
     [:h5
      {:class "font-semibold text-md"}
      (loc/tr :sso-configuration)]
     [:div
      {:class "flex flex-col gap-1"}
      [labelled-input/view
       {:data-type :data.type/keyword-multi
        :options login-page-config-schema/sso-login-methods-options
        :value (:login-page-config/sso-login-methods config)
        :label (loc/tr :login-page-config/sso-login-methods)
        :on-change (fn [value]
                     (on-change :login-page-config/sso-login-methods (set value))
                     (rf/dispatch [::event/set-field-touched :login-page-config/sso-login-methods]))
        :on-blur (fn []
                   (rf/dispatch [::event/set-field-touched :login-page-config/sso-login-methods]))}]

      (when (and (not has-azure?)
                 (not has-saml?)
                 (or show-all-errors?
                     (contains? touched-fields :login-page-config/sso-login-methods)))
        [:p
         {:class "text-sm m-0 text-danger-regular"}
         (loc/tr :add-at-least-one-sso-login-method)])]

     (when has-azure?
       [azure-configuration-section
        {:config config
         :on-change on-change}])

     (when has-saml?
       [saml-configuration-section
        {:config config
         :on-change on-change}])]))

(defn branding-card
  [{:keys [config on-change]}]
  [:div {:class "flex flex-col gap-4 p-4 border border-base-light-border-paler rounded-lg"}
   [:h5
    {:class "font-semibold text-md"}
    (loc/tr :appearance)]
   [:div {:class "grid grid-cols-1 md:grid-cols-2 gap-4"}
    [:div
     [labelled-input/view
      {:data-type :data.type/text-simple
       :value (:login-page-config/logo-url config)
       :label (loc/tr :logo-url)
       :on-change #(on-change :login-page-config/logo-url %)}]]
    [:div
     [labelled-input/view
      {:data-type :data.type/text-simple
       :value (:login-page-config/banner-img-url config)
       :label (loc/tr :banner-image-url)
       :on-change #(on-change :login-page-config/banner-img-url %)}]]]

   [:div {:class "grid grid-cols-1 md:grid-cols-4 gap-4"}
    [:div
     {:class "md:col-span-2"}
     [labelled-input/view
      {:data-type :data.type/text-simple
       :value (:login-page-config/banner-img-xl-url config)
       :label (loc/tr :banner-image-url-xl)
       :on-change #(on-change :login-page-config/banner-img-xl-url %)}]]

    [:div
     [labelled-input/view
      {:data-type :data.type/text-simple
       :value (:login-page-config/button-text-color config)
       :label (loc/tr :button-text-color)
       :placeholder "#FFF"
       :on-change #(on-change :login-page-config/button-text-color %)}]]

    [:div
     [labelled-input/view
      {:data-type :data.type/text-simple
       :value (:login-page-config/button-background-color config)
       :label (loc/tr :button-background-color)
       :placeholder "#8ABF4C"
       :on-change #(on-change :login-page-config/button-background-color %)}]]]])

(defn form-actions
  []
  (let [new-config? @(rf/subscribe [::sub/new-config?])]
    [:div {:class "flex gap-2 justify-end"}
     [button/sm-secondary-hollow
      {:on-click #(rf/dispatch [::event/set-view-mode :list])}
      (loc/tr :cancel)]
     [button/sm-primary-solid
      {:on-click #(rf/dispatch [::event/save-config])}
      (if new-config?
        (loc/tr :create)
        (loc/tr :save))]]))

(defn form-main-content
  []
  (let [config @(rf/subscribe [::sub/form-config])
        on-change #(rf/dispatch [::event/update-field %1 %2])
        new-config? @(rf/subscribe [::sub/new-config?])]
    [:div {:class "flex flex-col gap-4 pb-24"}
     [:div {:class "flex justify-between items-center"}
      [:h2 {:class "font-semibold text-xl"}
       (if new-config?
         (loc/tr :create-login-page)
         (loc/tr :edit-login-page))]]
     (if (nil? config)
       [:div
        {:class "text-center py-8"}
        (loc/tr :loading-indicator)]
       [:div {:class "flex flex-col gap-4"}
        [basic-info-card
         {:config config
          :on-change on-change}]
        [sso-configuration-card
         {:config config
          :on-change on-change}]
        [branding-card
         {:config config
          :on-change on-change}]
        [form-actions]])]))

(defn feature-access?
  []
  (and (session/is-admin?)
       (session/has-feature-access? :feature/new-sso-2025)))

(defn sso-view
  []
  (r/create-class
   {:component-did-mount (fn []
                           (when (feature-access?)
                             (rf/dispatch-sync [::event/load-configs])))
    :component-will-unmount (fn []
                              (rf/dispatch [::event/cleanup]))
    :reagent-render
    (fn []
      (let [view-mode @(rf/subscribe [::sub/view-mode])]
        (if (feature-access?)
          (case view-mode
            :create [form-main-content]
            :edit [form-main-content]
            [list-main-content])
          [:div
           {:class "grow flex flex-col justify-center items-center"}
           (loc/tr :not-found-header)])))}))
